import { isEmpty } from 'lodash'
import React from 'react'
import { useSelector } from 'react-redux'

import ModalForm from 'app/components/ModalForm'
import constants from 'app/constants'
import { eventEmitter } from 'app/helpers'
import {
  dispatchUpdateCreateAnnouncementModalState,
  dispatchHandleCreateAnnouncement,
  dispatchResetAnnouncementCreationData,
  dispatchUpdateAnnouncementFieldError
} from 'app/store/dispatcher'
import {
  selectIsCreateAnnouncementModalOpen,
  selectIsAnnouncementRequestInProgress,
  selectAnnouncementFormData,
  selectAnnouncementErrors
} from 'app/store/selectors'

import AnnouncementForm from './components/AnnouncementForm'
import { AnnouncementFormFields } from './components/AnnouncementForm/config'

const CreateAnnouncement = ({
  editingAnnouncementUuid,
  onModalClose: onParentModalClose
}) => {
  const isCreateAnnouncementModalOpen = useSelector(
    selectIsCreateAnnouncementModalOpen
  )

  const isAnnouncementRequestInProgress = useSelector(
    selectIsAnnouncementRequestInProgress
  )

  const formData = useSelector(selectAnnouncementFormData)
  const fieldErrors = useSelector(selectAnnouncementErrors)

  const isEditMode = React.useMemo(() => {
    return !!editingAnnouncementUuid
  }, [editingAnnouncementUuid])

  const resetAllFieldErrors = React.useCallback(() => {
    AnnouncementFormFields(isEditMode).forEach((field) => {
      dispatchUpdateAnnouncementFieldError(field.name, null)
    })
  }, [isEditMode])

  const onModalClose = React.useCallback(() => {
    resetAllFieldErrors()
    dispatchUpdateCreateAnnouncementModalState(false)
    dispatchResetAnnouncementCreationData()
    if (onParentModalClose) {
      onParentModalClose()
    }
  }, [resetAllFieldErrors, onParentModalClose])

  // Reset errors when modal opens
  React.useEffect(() => {
    if (isCreateAnnouncementModalOpen) {
      resetAllFieldErrors()
    }
  }, [isCreateAnnouncementModalOpen, resetAllFieldErrors])

  // Reset errors when announcement type changes
  React.useEffect(() => {
    if (formData.type) {
      resetAllFieldErrors()
    }
  }, [formData.type, resetAllFieldErrors])

  const handleSubmit = React.useCallback(async () => {
    const errors = {}
    let hasErrors = false

    AnnouncementFormFields(isEditMode).forEach((field) => {
      if (field.conditionalRender && !field.conditionalRender(formData)) {
        return
      }
      if (field?.validator) {
        const fieldValue = formData[field.name]
        const error = field.validator(fieldValue, formData, isEditMode)

        if (error) {
          errors[field.name] = error
          hasErrors = true
        }
      }
    })
    Object.keys(errors).forEach((fieldName) => {
      dispatchUpdateAnnouncementFieldError(fieldName, errors[fieldName])
    })

    AnnouncementFormFields(isEditMode).forEach((field) => {
      if (!errors[field.name] && fieldErrors[field.name]) {
        dispatchUpdateAnnouncementFieldError(field.name, null)
      }
    })

    if (!isEmpty(errors)) {
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
      return
    }

    try {
      const submissionData = isEditMode
        ? { ...formData, uuid: editingAnnouncementUuid }
        : formData

      await dispatchHandleCreateAnnouncement(submissionData)
    } catch (error) {
      console.log('Error creating/updating announcement:', error)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
    }
  }, [formData, fieldErrors, isEditMode, editingAnnouncementUuid])

  return (
    <ModalForm
      buttonLabel="Proceed to set sequence"
      isModalOpen={isCreateAnnouncementModalOpen}
      onModalClose={onModalClose}
      modalTitle={
        isEditMode ? 'Edit App Announcement' : 'Create an App Announcement'
      }
      maxWidth="lg"
      fullWidth
      loading={isAnnouncementRequestInProgress}
      onSaveClick={handleSubmit}>
      <AnnouncementForm isEditMode={isEditMode} />
    </ModalForm>
  )
}

export default CreateAnnouncement
